import { SPECIES_COUNT } from "./species";
import { fbm } from "./noise";

/**
 * Environment grid over the island footprint. Elevation and the derived
 * moisture field are generated once from the seed (and shared with Phase 2
 * terrain). Light/crowding/seedbank evolve per tick.
 *
 * All fields are flat Float32Arrays indexed `gz * size + gx`.
 */
export interface Grid {
  readonly size: number;
  /** World metres spanned by the grid on each axis. */
  readonly worldSize: number;
  /** Elevation (metres) at/below which a cell is open water. */
  readonly waterLevel: number;
  readonly elevation: Float32Array;
  readonly moisture: Float32Array;
  readonly light: Float32Array;
  /** 1 where the cell is dry land (plantable), 0 where open water. */
  readonly land: Uint8Array;
  /** Per-species conspecific crowding, laid out [species * cells + cell]. */
  readonly crowding: Float32Array;
  /** Per-species dormant seed counts, laid out [species * cells + cell]. */
  readonly seedbank: Float32Array;
}

export function createGrid(size: number, worldSize: number, waterLevel: number): Grid {
  const cells = size * size;
  return {
    size,
    worldSize,
    waterLevel,
    elevation: new Float32Array(cells),
    moisture: new Float32Array(cells),
    light: new Float32Array(cells),
    land: new Uint8Array(cells),
    crowding: new Float32Array(cells * SPECIES_COUNT),
    seedbank: new Float32Array(cells * SPECIES_COUNT),
  };
}

export function worldToCell(grid: Grid, worldX: number, worldZ: number): { gx: number; gz: number } {
  const half = grid.worldSize * 0.5;
  const gx = clampInt(Math.floor(((worldX + half) / grid.worldSize) * grid.size), grid.size);
  const gz = clampInt(Math.floor(((worldZ + half) / grid.worldSize) * grid.size), grid.size);
  return { gx, gz };
}

function clampInt(v: number, size: number): number {
  return v < 0 ? 0 : v >= size ? size - 1 : v;
}

/**
 * Generate the heightmap and derive moisture. Elevation is an island dome
 * (radial falloff) modulated by fBm hills; low areas below `waterLevel` become
 * lochs. Moisture is high near/below water and decays with height above water
 * and distance from it — with low hollows accumulating. This heterogeneity is
 * load-bearing: it's what lets each species win *somewhere*.
 */
export function generateTerrain(grid: Grid, seed: number, maxHeight = 7.5): void {
  const { size, worldSize } = grid;

  // --- elevation ---
  for (let gz = 0; gz < size; gz++) {
    for (let gx = 0; gx < size; gx++) {
      const i = gz * size + gx;
      const wx = (gx + 0.5) / size; // 0..1
      const wz = (gz + 0.5) / size;
      const dx = wx - 0.5;
      const dz = wz - 0.5;
      // Wavy coastline: the island radius varies with bearing, so the shore isn't
      // a clean circle — bays and headlands (deterministic from the seed).
      const ang = Math.atan2(dz, dx);
      const coastR = 0.5 + 0.055 * Math.sin(ang * 3.0 + seed * 0.7) + 0.035 * Math.sin(ang * 6.0 - seed * 0.3);
      const dist = Math.sqrt(dx * dx + dz * dz) / coastR;
      const dome = Math.max(0, 1 - dist * dist);
      // Rolling hills, plus a ridged component for ridgelines and valleys.
      const hills = fbm(wx, wz, seed, 6, 3.2);
      const rn = fbm(wx, wz, seed + 917, 4, 2.4);
      const ridge = 1.0 - Math.abs(rn * 2.0 - 1.0); // sharp crests where rn ≈ 0.5
      // Combine: dome sets the island; hills add relief; ridges raise crests inland.
      let e = dome * 0.82 + hills * 0.5 + ridge * ridge * 0.4 * dome - 0.42;
      e = Math.max(0, e) * maxHeight;
      grid.elevation[i] = e;
      grid.land[i] = e > grid.waterLevel ? 1 : 0;
    }
  }

  // --- distance to nearest water (chamfer transform), in cell units ---
  const INF = 1e9;
  const dist = new Float32Array(size * size);
  for (let i = 0; i < dist.length; i++) dist[i] = grid.land[i] === 0 ? 0 : INF;
  const relax = (i: number, j: number, w: number): void => {
    const d = dist[j]! + w;
    if (d < dist[i]!) dist[i] = d;
  };
  // forward pass
  for (let gz = 0; gz < size; gz++) {
    for (let gx = 0; gx < size; gx++) {
      const i = gz * size + gx;
      if (gx > 0) relax(i, i - 1, 1);
      if (gz > 0) relax(i, i - size, 1);
      if (gx > 0 && gz > 0) relax(i, i - size - 1, 1.4142);
      if (gx < size - 1 && gz > 0) relax(i, i - size + 1, 1.4142);
    }
  }
  // backward pass
  for (let gz = size - 1; gz >= 0; gz--) {
    for (let gx = size - 1; gx >= 0; gx--) {
      const i = gz * size + gx;
      if (gx < size - 1) relax(i, i + 1, 1);
      if (gz < size - 1) relax(i, i + size, 1);
      if (gx < size - 1 && gz < size - 1) relax(i, i + size + 1, 1.4142);
      if (gx > 0 && gz < size - 1) relax(i, i + size - 1, 1.4142);
    }
  }

  // --- moisture from height above water + distance to water ---
  const cellMetres = worldSize / size;
  for (let i = 0; i < size * size; i++) {
    if (grid.land[i] === 0) {
      grid.moisture[i] = 1;
      continue;
    }
    const heightAbove = grid.elevation[i]! - grid.waterLevel;
    const distMetres = dist[i]! * cellMetres;
    // Decay with both height and lateral distance; hollows (low height) stay wet.
    const byHeight = Math.exp(-heightAbove / 2.0);
    const byDist = Math.exp(-distMetres / 12.0);
    grid.moisture[i] = clamp01(0.15 + 0.85 * Math.max(byHeight * 0.6 + byDist * 0.4, byDist));
  }
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Bilinearly sample a flat grid field at a world (x, z). */
export function sampleField(grid: Grid, field: ArrayLike<number>, worldX: number, worldZ: number): number {
  const { size, worldSize } = grid;
  const half = worldSize * 0.5;
  const fx = ((worldX + half) / worldSize) * size - 0.5;
  const fz = ((worldZ + half) / worldSize) * size - 0.5;
  const x0 = Math.floor(fx);
  const z0 = Math.floor(fz);
  const tx = fx - x0;
  const tz = fz - z0;
  const cx0 = clampInt(x0, size);
  const cx1 = clampInt(x0 + 1, size);
  const cz0 = clampInt(z0, size);
  const cz1 = clampInt(z0 + 1, size);
  const v00 = field[cz0 * size + cx0]!;
  const v10 = field[cz0 * size + cx1]!;
  const v01 = field[cz1 * size + cx0]!;
  const v11 = field[cz1 * size + cx1]!;
  const a = v00 + (v10 - v00) * tx;
  const b = v01 + (v11 - v01) * tx;
  return a + (b - a) * tz;
}

/** Ground height (elevation, clamped to at least the water surface) at a world point. */
export function sampleElevation(grid: Grid, worldX: number, worldZ: number): number {
  return sampleField(grid, grid.elevation, worldX, worldZ);
}
